import { Auth0Provider, OAuthError, useAuth0 } from '@auth0/auth0-react';
import Config from '@config';
import useAppNavigate from '@effects/useAppNavigate';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import React, { useEffect, useRef } from 'react';

const DEFAULT_REFRESH_THRESHOLD = 30 * 60; // 30 min threshhold

/**
 * Remaining life of an access token in seconds, from its JWT exp claim.
 * A cached token's expires_in reports the ORIGINAL lifetime, not what is left,
 * so the refresh timer must be anchored to exp. Returns null when the token
 * is absent or not a decodable JWT, so callers can fall back to expires_in.
 */
export const getRemainingTokenSeconds = (accessToken: string | undefined): number | null => {
    try {
        const { exp } = jwtDecode<JwtPayload>(accessToken ?? '');
        return typeof exp === 'number' ? exp - Math.floor(Date.now() / 1000) : null;
    } catch {
        return null;
    }
};

export const AccessTokenMonitor = ({ threshholdInSeconds }: { threshholdInSeconds?: number }) => {
    const { getAccessTokenSilently, isAuthenticated, loginWithRedirect } = useAuth0();
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const threshhold = threshholdInSeconds || DEFAULT_REFRESH_THRESHOLD;

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        const setTokenRefresherTimer = async () => {
            try {
                // Read expires_in from the cached token rather than forcing a refresh
                // (cacheMode: 'off'). Forcing one here rotated the refresh token on every
                // page load; a load interrupted mid-exchange left a consumed token behind,
                // which trips Auth0's rotation reuse detection (403) and revokes the grant.
                // The scheduled refresh below still uses cacheMode: 'off'.
                const token = await getAccessTokenSilently({
                    detailedResponse: true,
                });

                const expiresIn = token.expires_in;
                // Anchor the timer to the token's actual expiry: a cached token's
                // expires_in is its original lifetime, so an aged session would
                // otherwise schedule the refresh long after the token had expired.
                // Clamped at zero: inside the threshold window the refresh is
                // already due, so it fires immediately.
                const remaining = getRemainingTokenSeconds(token.access_token) ?? expiresIn;
                const timeToRefresh = Math.max(remaining - threshhold, 0);

                if (expiresIn - threshhold > 0) {
                    if (timer.current) {
                        clearTimeout(timer.current);
                    }

                    timer.current = setTimeout(async () => {
                        try {
                            await getAccessTokenSilently({
                                cacheMode: 'off',
                            });

                            setTokenRefresherTimer();
                        } catch (e) {
                            if (
                                e instanceof OAuthError &&
                                (e.error === 'missing_refresh_token' || e.error === 'invalid_grant')
                            ) {
                                loginWithRedirect();
                            }
                        }
                    }, timeToRefresh * 1000);
                }
            } catch (e) {
                if (e instanceof OAuthError && (e.error === 'missing_refresh_token' || e.error === 'invalid_grant')) {
                    loginWithRedirect();
                }
            }
        };

        setTokenRefresherTimer();

        return () => {
            if (timer.current) {
                clearTimeout(timer.current);
            }
        };
    }, [isAuthenticated, getAccessTokenSilently, loginWithRedirect, threshhold]);

    return null;
};

export const Auth0ProviderWithHistory: React.FC<{ children?: any }> = ({ children }) => {
    const navigate = useAppNavigate();

    const onRedirectCallback = (appState: any) => {
        navigate(appState?.returnTo || window.location.pathname, { replace: true });
    };

    return (
        <Auth0Provider
            domain={Config.AUTH0_DOMAIN}
            clientId={Config.AUTH0_CLIENTID}
            authorizationParams={{
                redirect_uri: window.location.origin + '/callback',
                audience: Config.AUTH0_AUDIENCE,
            }}
            useRefreshTokens={true}
            cacheLocation="localstorage"
            onRedirectCallback={onRedirectCallback}
            useRefreshTokensFallback={true}
        >
            {children}
        </Auth0Provider>
    );
};
